import React from 'react';
import { FiUpload, FiX, FiImage } from 'react-icons/fi';

const ImagePickerField = ({
    label,
    preloadedImage,
    onImageSelect,
    onClear,
    placeholder = "Click to upload image",
    acceptedFormats = ".jpg,.png,.jpeg",
    maxFileSize = 10, // in MB
    disabled = false,
    required = false,
    error = false,
    helperText,
}) => {
    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > maxFileSize * 1024 * 1024) {
            alert(`File size exceeds ${maxFileSize}MB limit`);
            return;
        }
        onImageSelect(file);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onClear?.();
    };

    return (
        <div className="form-control w-full">
            {/* Label */}
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            {/* Field Container */}
            <div className={`relative border rounded-lg px-4 py-2.5 shadow-sm transition-all duration-200 ${
                disabled 
                    ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                    : error 
                        ? 'border-red-300 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/30' 
                        : 'border-gray-300 hover:border-gray-400 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/50'
            }`}>

                {/* Display selected image info */}
                {preloadedImage ? (
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-gray-100 overflow-hidden flex items-center justify-center">
                            <img
                                src={typeof preloadedImage === 'string' ? preloadedImage : URL.createObjectURL(preloadedImage)}
                                alt="Preview"
                                className="object-cover w-full h-full"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                                {preloadedImage.name || "Selected image"}
                            </p>
                            <p className="text-xs text-gray-500">
                                {Math.round((preloadedImage.size / 1024) * 100) / 100} KB
                            </p>
                        </div>
                        {!disabled && onClear && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                            >
                                <FiX size={16} />
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center gap-2 text-gray-500">
                        <FiImage size={18} />
                        <span className="text-sm truncate">{placeholder}</span>
                    </div>
                )}

                {/* Upload Button (hidden input) */}
                {!disabled && (
                    <label className="absolute inset-0 cursor-pointer">
                        <input
                            type="file"
                            className="hidden"
                            accept={acceptedFormats}
                            onChange={handleImageSelect}
                            disabled={disabled}
                        />
                    </label>
                )}
            </div>

            {/* Helper text / Error message */}
            {helperText && (
                <p className={`mt-1 text-xs ${error ? 'text-red-600' : 'text-gray-500'}`}>
                    {helperText}
                </p>
            )}
        </div>
    );
};

export default ImagePickerField;